import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ApexOptions } from 'ng-apexcharts';
import { SharedModule } from 'src/app/layouts/full/shared/shared.module';
import { Analysis, AnalysisType } from 'src/app/services/sensingtex.service';
import { PressureMapComponent } from '../pressure-map/pressure-map.component';

interface PressureAnalysis {
  time?: number, pressures?: { x: number, y: number, weight: number }[]
}

interface FootAnalysis {
  data?: PressurePoint[];
  length: number,
  width: {
    forefoot: number,
    rearfoot: number,
  },
  stats: {
    weightDistributionInPercentage: number,
    footArea: number,
    averagePressure: number
  },
  swing?: {
    heelPoints: {
      pressures: PressurePoint[]
    },
    takeOff: {
      pressures: PressurePoint[]
    }
  },
  pointsToDraw?: { x: number, y: number }[]
}

interface PressurePoint {
  x: number,
  y: number,
  weight: number
}


enum Foot {
  left = "left",
  right = "right"
}

@Component({
  selector: 'app-analysis',
  standalone: true,
  imports: [SharedModule, PressureMapComponent],
  templateUrl: './analysis.component.html',
  styleUrl: './analysis.component.scss'
})
export class AnalysisComponent {
  @ViewChild("myCanvas") myCanvas: ElementRef<HTMLCanvasElement>;
  @Input() analysis: Analysis;
  @Input() showActions: boolean;
  @Input() orderData;
  @Input() generatingPDF: boolean;
  @Input() alreadyInverted: boolean;
  @Input() rotate180: boolean;
  @Output() loaded = new EventEmitter<void>()
  width = 200;

  mappedToPressureMapStatic: PressureAnalysis | PressureAnalysis[]
  mappedToPressureMapDynamicLeft: PressureAnalysis | PressureAnalysis[]
  mappedToPressureMapDynamicRight: PressureAnalysis | PressureAnalysis[]
  mappedToPressureMapBalance: PressureAnalysis | PressureAnalysis[]

  feetAnalysis: {
    left: FootAnalysis,
    right: FootAnalysis
  };

  weightDistribution: { leftPercentage: number; rightPercentage: number; };

  // Charts init
  averagePressuresChartOptions: ApexOptions;
  maxPressuresChartOptions: ApexOptions;
  leftCenterOfForceChartOptions: ApexOptions;
  rightCenterOfForceChartOptions: ApexOptions;
  bodyChartOptions: ApexOptions;
  lateralBalanceChartOptions: ApexOptions
  frontBackBalanceChartOptions: ApexOptions

  leftCenterOfForceData: { y: number, x: number }[];
  rightCenterOfForceData: { y: number, x: number }[];
  bodyData: any[];

  mappedToPressureMapDynamicLeftHeel = [];
  mappedToPressureMapDynamicLeftFootFlat = [];
  mappedToPressureMapDynamicLefTakeOff = [];

  filteredActivePointsLeft
  filteredActivePointsRight


  constructor() {

    // Init analysis 
    this.feetAnalysis = {
      left: {
        length: 0,
        stats: {
          averagePressure: 0,
          footArea: 0,
          weightDistributionInPercentage: 0
        },
        width: {
          forefoot: 0,
          rearfoot: 0
        },
        data: [],
        pointsToDraw: []
      },
      right: {
        length: 0,
        stats: {
          averagePressure: 0,
          footArea: 0,
          weightDistributionInPercentage: 0
        },
        width: {
          forefoot: 0,
          rearfoot: 0
        },
        data: [],
        pointsToDraw: []
      }
    }
  }

  ngOnInit(): void {
    this.mapToOld();

    if (this.showActions) {
      this.width = 600;
    }
    this.loaded.emit();
  }

  invertFootData(pressurePoints: { x: number, y: number, z: number }[][], invert: { x: boolean, y: boolean }): { x: number, y: number, z: number }[][] {

    let totalSensorsPerRow = 48.0;

    let newInvertedPoints: { x: number, y: number, z: number }[][] = [];

    pressurePoints.map((capture) => {
      let newCapture = [];

      capture.map((point) => {
        if (invert.x) {
          point.x = totalSensorsPerRow - point.x
        }
        if (invert.y) {
          point.y = totalSensorsPerRow - point.y
        }

        newCapture.push({
          x: point.x,
          y: point.y,
          z: point.z
        });

      })
      newInvertedPoints.push(newCapture)
    })

    return pressurePoints;
  }

  mapToOld() {

    if (this.analysis.type === "Dynamic") {

      let { timeStampLeft, timeStampRight, activePointsLeft, activePointsRight } = this.getSensingtexData(AnalysisType.Dynamic);

      this.filteredActivePointsLeft = activePointsLeft.map(points => points.filter(point => point.z > 20));
      this.filteredActivePointsRight = activePointsRight.map(points => points.filter(point => point.z > 20));

      this.splitFeetData();

      let mappedDataLeft: PressureAnalysis[] = this.addTimestampToPressure(timeStampLeft, this.filteredActivePointsLeft);
      let mappedDataRight: PressureAnalysis[] = this.addTimestampToPressure(timeStampRight, this.filteredActivePointsRight);

      this.mappedToPressureMapDynamicLeft = mappedDataLeft
      this.mappedToPressureMapDynamicRight = mappedDataRight

      let leftFootData = this.getFootData(Foot.left);
      let rightFootData = this.getFootData(Foot.right);

      this.feetAnalysis.right.length = this.calculateFootLength(rightFootData);
      this.feetAnalysis.left.length = this.calculateFootLength(leftFootData);

      let weightDistribution = this.calculateWeightDistribution(leftFootData, rightFootData)

      this.feetAnalysis.left.stats = {
        averagePressure: 0,
        footArea: this.calculateFootArea(leftFootData),
        weightDistributionInPercentage: weightDistribution.leftPercentage
      }

      let leftFootWidths = this.calculateWidth(leftFootData, Foot.left);

      this.feetAnalysis.left.width = {
        forefoot: leftFootWidths.forefoot,
        rearfoot: leftFootWidths.rearfoot
      }

      this.feetAnalysis.right.stats = {
        averagePressure: 0,
        footArea: this.calculateFootArea(rightFootData),
        weightDistributionInPercentage: weightDistribution.rightPercentage
      }
      this.feetAnalysis.right.width = {
        forefoot: this.calculateWidth(rightFootData, Foot.right).forefoot,
        rearfoot: this.calculateWidth(rightFootData, Foot.right).rearfoot
      }

      this.mapDataToCharts(AnalysisType.Dynamic)

    }

    if (this.analysis.type === "Static") {

      let pressuresMapped: PressureAnalysis = {
        pressures: []
      };

      this.analysis.mapPreview.map((YRow, YIndex) => {
        YRow.map((XValue, XIndex) => {

          if (XValue > 0) {
            pressuresMapped.pressures.push({
              x: XIndex,
              y: YIndex,
              weight: XValue
            })

            this.feetAnalysis.left.pointsToDraw.push({
              x: XIndex,
              y: YIndex
            });
          }

        })
      })


      let transforDataToContainZ = [];
      transforDataToContainZ.push(pressuresMapped.pressures.map((pressure) => {
        return {
          x: pressure.x,
          y: pressure.y,
          z: pressure.weight
        }
      }))

      let invertedData = this.invertFootData(transforDataToContainZ, { x: false, y: false })

      let invertedDataMapped = invertedData[0].map((pressureInverted) => {
        return {
          x: pressureInverted.x,
          y: pressureInverted.y,
          weight: pressureInverted.z,
        }
      })

      pressuresMapped.pressures = invertedDataMapped;

      this.mappedToPressureMapStatic = pressuresMapped;

      let pressurePointsByFoot = this.getPressurePointsByFoot(pressuresMapped);
      let leftFootData: PressurePoint[] = pressurePointsByFoot.left
      let rightFootData: PressurePoint[] = pressurePointsByFoot.right

      let weightDistribution = this.calculateWeightDistribution(leftFootData, rightFootData)

      this.feetAnalysis.left.length = this.calculateFootLength(leftFootData);
      this.feetAnalysis.right.length = this.calculateFootLength(rightFootData);

      this.feetAnalysis.left.stats = {
        averagePressure: 0,
        footArea: this.calculateFootArea(leftFootData),
        weightDistributionInPercentage: weightDistribution.leftPercentage
      }
      this.feetAnalysis.left.width = {
        forefoot: this.calculateWidth(leftFootData, Foot.left).forefoot,
        rearfoot: this.calculateWidth(leftFootData, Foot.left).rearfoot
      }

      this.feetAnalysis.right.stats = {
        averagePressure: 0,
        footArea: this.calculateFootArea(rightFootData),
        weightDistributionInPercentage: weightDistribution.rightPercentage
      }
      this.feetAnalysis.right.width = {
        forefoot: this.calculateWidth(rightFootData, Foot.right).forefoot,
        rearfoot: this.calculateWidth(rightFootData, Foot.right).rearfoot
      }
    }

    if (this.analysis.type === "Balance") {

      const totalDuration = 10;

      const mappedData = [];

      const pressures = this.analysis.mapPreview;
      const totalPoints = pressures.reduce((acc, row) => acc + row.length, 0);
      const timeIncrement = totalDuration / totalPoints;

      let currentTime = 0;

      pressures.forEach((YRow, YIndex) => {
        let pressuresArray = [];

        YRow.forEach((XValue, XIndex) => {
          if (XValue > 0) {
            pressuresArray.push({
              x: XIndex,
              y: YIndex,
              weight: XValue
            });
          }
        });

        if (pressuresArray.length > 0) {
          mappedData.push({
            time: currentTime,
            pressures: pressuresArray
          });
        }

        currentTime += timeIncrement;
      });

      let transforDataToContainZ = mappedData.map(data => {
        return {
          time: data.time,
          pressures: data.pressures.map((pressure) => {
            return {
              x: pressure.x,
              y: pressure.y,
              z: pressure.weight
            };
          })
        };
      });

      let invertedData = transforDataToContainZ.map(data => {
        let invertedPressures = this.invertFootData([data.pressures], { x: false, y: false })[0];
        return {
          time: data.time,
          pressures: invertedPressures.map((pressureInverted) => {
            return {
              x: pressureInverted.x,
              y: pressureInverted.y,
              weight: pressureInverted.z
            };
          })
        };
      });

      this.mappedToPressureMapBalance = invertedData;

      const {
        COMXLeftFoot,
        COMYLeftFoot,
        COMXRightFoot,
        COMYRightFoot,
        totalCOMX,
        totalCOMY } = this.getSensingtexData(AnalysisType.Balance);


      this.leftCenterOfForceData = COMXLeftFoot.map((x, index) => ({ x: x, y: COMYLeftFoot[index] }));
      this.rightCenterOfForceData = COMXRightFoot.map((x, index) => ({ x: x, y: COMYRightFoot[index] }));
      this.bodyData = totalCOMX.map((x, index) => ({ x: x, y: totalCOMY[index] }));

      const lateralBalanceData = [];

      let COMXLeftFootMin = Math.min(...COMXLeftFoot);
      let COMXRightFootMin = Math.min(...COMXRightFoot);
      let totalCOMXMin = Math.min(...totalCOMX);

      for (let i = 0; i < totalCOMX.length; i++) {
        const leftFootCenter: number = COMXLeftFoot[i] - COMXLeftFootMin;
        const rightFootCenter = COMXRightFoot[i] - COMXRightFootMin;
        const bodyCenter = totalCOMX[i] - totalCOMXMin;

        lateralBalanceData.push({
          time: i * timeIncrement,
          leftFootCenter,
          rightFootCenter,
          bodyCenter
        });
      }

      const frontBackBalanceData = [];

      let COMYLeftFootMin = Math.min(...COMYLeftFoot);
      let COMYRightFootMin = Math.min(...COMYRightFoot);
      let totalCOMYMin = Math.min(...totalCOMY);

      for (let i = 0; i < totalCOMX.length; i++) {
        const leftFootCenter: number = COMYLeftFoot[i] - COMYLeftFootMin;
        const rightFootCenter = COMYRightFoot[i] - COMYRightFootMin;
        const bodyCenter = totalCOMY[i] - totalCOMYMin;

        frontBackBalanceData.push({
          time: i * timeIncrement,
          leftFootCenter,
          rightFootCenter,
          bodyCenter
        });
      }

      let COM = {
        left: COMYLeftFoot,
        right: COMYRightFoot,
        total: totalCOMY,
        lateralBalance: lateralBalanceData,
        frontBackBalance: frontBackBalanceData
      }

      this.mapDataToCharts(AnalysisType.Balance, COM)

    }
  }

  getPressurePointsByFoot(pressures: PressureAnalysis): { left: PressurePoint[], right: PressurePoint[] } {

    let left: PressurePoint[] = [];
    let right: PressurePoint[] = [];

    pressures.pressures.map((pressure) => {
      if (pressure.x < 24) {
        left.push(pressure);
      } else {
        right.push(pressure);
      }
    })

    return {
      left,
      right
    }
  }

  getFootData(foot: Foot) {
    if (foot === "left") {
      return this.feetAnalysis.left.data;
    }

    return this.feetAnalysis.right.data;
  }

  splitFeetData() {

    this.feetAnalysis.left.data = this.mapPointsToPressurePoint(this.filteredActivePointsLeft)
    this.feetAnalysis.right.data = this.mapPointsToPressurePoint(this.filteredActivePointsRight)
  }

  mapDataToCharts(analysisType: AnalysisType, data?: { left: any, right: any, total: any, lateralBalance: any, frontBackBalance: any }) {

    if (analysisType === "Dynamic") {
      let averagePressuresLeft = this.analysis.additionalParameters[1][1];
      let averagePressuresRight = this.analysis.additionalParameters[7][1];

      let maxPressuresLeft = this.analysis.additionalParameters[2][1];
      let maxPressuresRight = this.analysis.additionalParameters[8][1];

      // Map data to Charts
      this.averagePressuresChartOptions = {
        series: [
          {
            name: 'Left Foot',
            data: averagePressuresLeft
          },
          {
            name: 'Right Foot',
            data: averagePressuresRight
          }
        ],
        chart: {
          type: 'line',
          width: 800,
          height: 250,
          toolbar: {
            show: false
          },
          animations: {
            enabled: false
          }
        },
        xaxis: {
          categories: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35],
        },
        yaxis: {
          forceNiceScale: true,
          labels: {
            formatter: function (value) {
              return Math.round(value).toString();
            }
          },
          title: {
            text: 'Values (KPa)'
          }
        },
        colors: ['#008FFB', '#00E396'],
        responsive: [
          { breakpoint: 1700, options: { chart: { width: 600 } } },
          { breakpoint: 1470, options: { chart: { width: 500 } } },
          { breakpoint: 1340, options: { chart: { width: 300 } } }
        ]
      };

      this.maxPressuresChartOptions = {
        series: [
          {
            name: 'Left Foot',
            data: maxPressuresLeft
          },
          {
            name: 'Right Foot',
            data: maxPressuresRight
          }
        ],
        chart: {
          type: 'line',
          width: 800,
          height: 250,
          toolbar: {
            show: false
          },
          animations: {
            enabled: false
          }
        },
        xaxis: {
          categories: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35]
        },
        yaxis: {
          forceNiceScale: true,
          labels: {
            formatter: function (value) {
              return Math.round(value).toString();
            }
          },
          title: {
            text: 'Values (KPa)'
          }
        },
        colors: ['#FF5733', '#33FF57'],
        responsive: [
          { breakpoint: 1700, options: { chart: { width: 600 } } },
          { breakpoint: 1470, options: { chart: { width: 500 } } },
          { breakpoint: 1340, options: { chart: { width: 300 } } }
        ]
      };
    }

    if (analysisType === "Balance") {

      this.leftCenterOfForceChartOptions = {
        series: [
          {
            name: 'Left Foot Center of Force',
            data: this.leftCenterOfForceData
          }
        ],
        chart: {
          type: 'scatter',
          width: 600,
          height: 500,
          toolbar: {
            show: false
          },
          animations: {
            enabled: false
          }
        },
        xaxis: {
          title: {
            text: "Sway " + (getSwayOnXandYAxisInCm(this.leftCenterOfForceData).x.toFixed(2) + " cm")
          },
          labels: {
            formatter: (value) => {
              return getSwayPoint(this.leftCenterOfForceData, { y: 0, x: parseFloat(value) }).x.toFixed(2).toString() + "cm";
            },
            show: false
          }
        },
        yaxis: {
          min: Math.min(...this.leftCenterOfForceData.map((force) => force.y)),
          forceNiceScale: true,
          labels: {
            formatter: (value) => {
              return getSwayPoint(this.leftCenterOfForceData, { y: value, x: 0 }).y.toFixed(2).toString() + "cm";
            },
          },
          title: {
            text: "Sway " + (getSwayOnXandYAxisInCm(this.leftCenterOfForceData).y.toFixed(2) + " cm")
          },
        },
        responsive: [
          { breakpoint: 1700, options: { chart: { width: 600 } } },
          { breakpoint: 1470, options: { chart: { width: 500 } } },
          { breakpoint: 1340, options: { chart: { width: 300 } } }
        ]

      };

      this.rightCenterOfForceChartOptions = {
        series: [
          {
            name: 'Right Foot Center of Force',
            data: this.rightCenterOfForceData
          }
        ],
        chart: {
          type: 'scatter',
          width: 600,
          height: 500,
          toolbar: {
            show: false
          },
          animations: {
            enabled: false
          }
        },
        xaxis: {
          title: {
            text: "Sway " + (getSwayOnXandYAxisInCm(this.rightCenterOfForceData).x.toFixed(2) + " cm")
          },
          labels: {
            formatter: (value) => {
              return getSwayPoint(this.rightCenterOfForceData, { y: 0, x: parseFloat(value) }).x.toFixed(2).toString() + "cm";
            },
            show: false
          }
        },
        yaxis: {
          min: Math.min(...this.rightCenterOfForceData.map((force) => force.y)),
          forceNiceScale: true,
          labels: {
            formatter: (value) => {
              return getSwayPoint(this.rightCenterOfForceData, { y: value, x: 0 }).y.toFixed(2).toString() + "cm";
            },
            show: false
          },
          title: {
            text: "Sway " + (getSwayOnXandYAxisInCm(this.rightCenterOfForceData).y.toFixed(2) + " cm")
          }
        },
        responsive: [
          { breakpoint: 1700, options: { chart: { width: 600 } } },
          { breakpoint: 1470, options: { chart: { width: 500 } } },
          { breakpoint: 1340, options: { chart: { width: 300 } } }
        ]
      };

      this.bodyChartOptions = {
        series: [
          {
            name: 'Body Center of Force',
            data: this.bodyData
          }
        ],
        chart: {
          type: 'scatter',
          width: 600,
          height: 500,
          toolbar: {
            show: false
          },
          animations: {
            enabled: false
          }
        },
        xaxis: {
          title: {
            text: "Sway " + (getSwayOnXandYAxisInCm(this.bodyData).x.toFixed(2) + " cm")
          },
          labels: {
            formatter: (value) => {
              return getSwayPoint(this.bodyData, { y: 0, x: parseFloat(value) }).x.toFixed(2).toString() + "cm";
            },
            show: false
          }
        },
        yaxis: {
          min: Math.min(...this.bodyData.map((force) => force.y)),
          forceNiceScale: true,
          labels: {
            formatter: (value) => {
              return getSwayPoint(this.bodyData, { y: value, x: 0 }).y.toFixed(2).toString() + "cm";
            },
            show: false
          },
          title: {
            text: "Sway " + (getSwayOnXandYAxisInCm(this.bodyData).y.toFixed(2) + " cm")
          }
        },
        responsive: [
          { breakpoint: 1700, options: { chart: { width: 600 } } },
          { breakpoint: 1470, options: { chart: { width: 500 } } },
          { breakpoint: 1340, options: { chart: { width: 300 } } }
        ]
      };

      this.lateralBalanceChartOptions = {
        series: [
          { name: "Left Foot", data: data.lateralBalance.map(({ time, leftFootCenter }) => [time * 20.4, leftFootCenter]) },
          { name: "Right Foot", data: data.lateralBalance.map(({ time, rightFootCenter }) => [time * 20.4, rightFootCenter]) },
          { name: "Body", data: data.lateralBalance.map(({ time, bodyCenter }) => [time * 20.4, bodyCenter]) }
        ],
        chart: {
          type: "line",
          width: 800,
          height: 300,
          toolbar: { show: false },
          animations: {
            enabled: false
          }
        },
        xaxis: { type: 'numeric', title: { text: 'Seconds' } },
        yaxis: {
          forceNiceScale: true,
          labels: { formatter: value => Math.round(value).toString() },
          title: { text: "Centimeters" }
        },
        colors: ["#FF5733", "#3385FF", "#33FF6B"],
        responsive: [
          { breakpoint: 1700, options: { chart: { width: 600 } } },
          { breakpoint: 1470, options: { chart: { width: 500 } } },
          { breakpoint: 1340, options: { chart: { width: 300 } } }
        ]
      };

      this.frontBackBalanceChartOptions = {
        series: [
          { name: "Left Foot", data: data.frontBackBalance.map(({ time, leftFootCenter }) => [time * 20.4, leftFootCenter]) },
          { name: "Right Foot", data: data.frontBackBalance.map(({ time, rightFootCenter }) => [time * 20.4, rightFootCenter]) },
          { name: "Body", data: data.frontBackBalance.map(({ time, bodyCenter }) => [time * 20.4, bodyCenter]) }
        ],
        chart: {
          type: "line",
          width: 800,
          height: 300,
          toolbar: { show: false },
          animations: {
            enabled: false
          }
        },
        xaxis: { type: "numeric", title: { text: 'Seconds' } },
        yaxis: {
          forceNiceScale: true,
          labels: { formatter: function (value) { return Math.round(value).toString(); } },
          title: { text: "Centimeters" }
        },
        colors: ["#FF5733", "#3385FF", "#33FF6B"],
        responsive: [
          { breakpoint: 1700, options: { chart: { width: 600 } } },
          { breakpoint: 1470, options: { chart: { width: 500 } } },
          { breakpoint: 1340, options: { chart: { width: 300 } } }
        ]
      }
    }

    function getSwayOnXandYAxisInCm(points: { x: number, y: number }[]) {
      return {
        x: Math.max(...points.map((force) => force.x)) - Math.min(...points.map((force) => force.x)),
        y: Math.max(...points.map((force) => force.y)) - Math.min(...points.map((force) => force.y))
      }
    }

    function getSwayPoint(points: { x: number, y: number }[], actualPoint: { x: number, y: number }) {
      return {
        x: actualPoint.x - Math.min(...points.map((force) => force.x)),
        y: actualPoint.y - Math.min(...points.map((force) => force.y))
      }
    }
  }

  getSensingtexData(analysisType: AnalysisType): {
    timeStampLeft?,
    timeStampRight?,
    activePointsLeft?,
    activePointsRight?,
    averagePressuresLeft?,
    averagePressuresRight?,
    maxPressuresLeft?,
    maxPressuresRight?,
    COMXLeftFoot?,
    COMYLeftFoot?,
    COMXRightFoot?,
    COMYRightFoot?,
    totalCOMX?,
    totalCOMY?
  } {


    if (analysisType === "Dynamic") {
      // Invert the data because it's flipped on Y and X
      let activePointsLeft;
      let activePointsRight;

      activePointsLeft = this.analysis.additionalParameters[12]["activePointsLeft"]
      activePointsRight = this.analysis.additionalParameters[13]["activePointsRight"]

      return {
        timeStampLeft: this.analysis.additionalParameters[5][1],
        timeStampRight: this.analysis.additionalParameters[11][1],
        activePointsLeft,
        activePointsRight,
        averagePressuresLeft: this.analysis.additionalParameters[1][1],
        averagePressuresRight: this.analysis.additionalParameters[7][1],
        maxPressuresLeft: this.analysis.additionalParameters[2][1],
        maxPressuresRight: this.analysis.additionalParameters[8][1]
      };
    }

    if (analysisType === "Balance") {
      return {
        COMXLeftFoot: this.analysis.additionalParameters[2][1],
        COMYLeftFoot: this.analysis.additionalParameters[3][1],
        COMXRightFoot: this.analysis.additionalParameters[0][1],
        COMYRightFoot: this.analysis.additionalParameters[1][1],
        totalCOMX: this.analysis.additionalParameters[4][1],
        totalCOMY: this.analysis.additionalParameters[5][1],
      }
    }

    return {
      COMXLeftFoot: null,
      COMYLeftFoot: null,
      COMXRightFoot: null,
      COMYRightFoot: null,
      totalCOMX: null,
      totalCOMY: null,
    }
  }

  addTimestampToPressure(timeStamp, activePoints): PressureAnalysis[] {

    let pressureAnalysis: PressureAnalysis[] = [];

    timeStamp.map((time, index) => {
      if (activePoints[index].length > 0) {
        pressureAnalysis.push({
          time: time,
          pressures: activePoints[index].map((point) => {
            return { ...point, weight: point.z }
          })
        })
      }
    })

    return pressureAnalysis;
  }

  mapPointsToPressurePoint(activePoints): PressurePoint[] {
    let pressurePoints: PressurePoint[] = [];

    activePoints.forEach(points => {
      points.forEach(point => {
        pressurePoints.push({ x: point.x, y: point.y, weight: point.z });
      });
    });

    return pressurePoints;
  }

  calculatePressureSwing(leftFootData, rightFootData) {
    let minY = Number.MAX_VALUE;
    let maxY = Number.MIN_VALUE;

    leftFootData.forEach(point => {
      minY = Math.min(minY, point.y);
      maxY = Math.max(maxY, point.y);
    });

    // Del largo de la huella obtener el 25%, que se considera la zona del talón
    let heelAreaInCm = this.feetAnalysis.left.length - (this.feetAnalysis.left.length * 0.75);
    // Del mínimo de Y le sumamos los cm que está definida como zona de talón
    let heelAreaMaxY = minY + heelAreaInCm;

    let heelPoints = leftFootData.filter(point => point.y <= heelAreaMaxY)

    // Del largo de la huella obtener el 75%, que se considera la zona del forefoot
    let takeOffAreaInCm = this.feetAnalysis.left.length - (this.feetAnalysis.left.length * 0.25)
    // Del mínimo de Y le sumamos los cm que está definida como zona de talón
    let takeOffAreaMaxY = minY + takeOffAreaInCm;

    let takeOffPoints = leftFootData.filter(point => point.y >= takeOffAreaMaxY)

    this.feetAnalysis.left.swing = {
      heelPoints: {
        pressures: heelPoints
      },
      takeOff: {
        pressures: takeOffPoints
      }
    }

    let flatFootAreaInCm = this.feetAnalysis.left.length - (heelAreaInCm - takeOffAreaInCm)



    let activePointsLeft = this.analysis.additionalParameters[12]["activePointsLeft"]

    let maxActivePointsLeft = 0
    let maxActivePointsPressureLeft

    leftFootData.map((pressure, index) => {
      if (pressure.length > maxActivePointsLeft) {
        maxActivePointsPressureLeft = pressure
      }
    })

    this.mappedToPressureMapDynamicLeftHeel = maxActivePointsPressureLeft

  }

  calculateFootLength(footData): number {

    let minY = Number.MAX_VALUE;
    let maxY = Number.MIN_VALUE;

    footData.forEach(point => {
      minY = Math.min(minY, point.y);
      maxY = Math.max(maxY, point.y);
    });


    const distanceY = maxY - minY;


    const lengthInCentimeters = distanceY;

    return lengthInCentimeters;
  }


  calculateWidth(data: { x: number, y: number }[], foot: Foot): { forefoot: number, rearfoot: number } {

    let footLength = foot == "left" ? this.feetAnalysis.left.length : this.feetAnalysis.right.length;

    let heelDivisionLengthNumber = footLength - (footLength * 0.45);
    let forefootDivisionLengthNumber = footLength - (footLength * 0.25);



    let referencePoints = {
      rearfoot: {
        minXPoint: { x: Number.MAX_VALUE, y: 0 },
        maxXPoint: { x: Number.MIN_VALUE, y: 0 },
        minYpoint: { x: 0, y: Number.MAX_VALUE },
        maxYpoint: { x: 0, y: Number.MIN_VALUE },
      },
      forefoot: {
        minXPoint: { x: Number.MAX_VALUE, y: 0 },
        maxXPoint: { x: Number.MIN_VALUE, y: 0 },
        minYpoint: { x: 0, y: Number.MAX_VALUE },
        maxYpoint: { x: 0, y: Number.MIN_VALUE },
      }
    }

    data.forEach(point => {
      if (point.y > referencePoints.rearfoot.maxYpoint.y) {
        referencePoints.rearfoot.maxYpoint = point
      }
      if (point.y < referencePoints.rearfoot.minYpoint.y) {
        referencePoints.rearfoot.minYpoint = point
      }
    });

    if (foot === Foot.left) {
      this.feetAnalysis.left.pointsToDraw.push(referencePoints.rearfoot.minYpoint)
      this.feetAnalysis.left.pointsToDraw.push(referencePoints.forefoot.minYpoint)
    } else {
      this.feetAnalysis.right.pointsToDraw.push(referencePoints.rearfoot.minYpoint)
      this.feetAnalysis.right.pointsToDraw.push(referencePoints.forefoot.minYpoint)

    }

    let divisionHeel = (referencePoints.rearfoot.minYpoint.y + heelDivisionLengthNumber);
    // if (foot === Foot.left){
    //   this.feetAnalysis.left.pointsToDraw.push({x: 2, y: 24})
    // }
    let divisionForefoot = (referencePoints.rearfoot.minYpoint.y + forefootDivisionLengthNumber);

    // Get min and max width of heel and forefoot
    data.forEach(point => {

      // if the point is under the heel line
      if (point.y < divisionHeel) {

        if (point.x > referencePoints.rearfoot.maxXPoint.x) {
          referencePoints.rearfoot.maxXPoint = point
        }
        if (point.x < referencePoints.rearfoot.minXPoint.x) {
          referencePoints.rearfoot.minXPoint = point
        }
      }

      // If its over the division line of forefoot do the calculation
      if (point.y > divisionForefoot) {
        if (point.x > referencePoints.forefoot.maxXPoint.x) {
          referencePoints.forefoot.maxXPoint = point
        }
        if (point.x < referencePoints.forefoot.minXPoint.x) {
          referencePoints.forefoot.minXPoint = point
        }
      }
    });

    if (foot == "left" && this.showActions) {
      this.feetAnalysis.left.pointsToDraw.push(referencePoints.forefoot.maxXPoint);
      this.feetAnalysis.left.pointsToDraw.push(referencePoints.forefoot.minXPoint);
      this.feetAnalysis.left.pointsToDraw.push(referencePoints.rearfoot.maxXPoint);
      this.feetAnalysis.left.pointsToDraw.push(referencePoints.rearfoot.minXPoint);
    } else {
      this.feetAnalysis.right.pointsToDraw.push(referencePoints.forefoot.maxXPoint);
      this.feetAnalysis.right.pointsToDraw.push(referencePoints.forefoot.minXPoint);
      this.feetAnalysis.right.pointsToDraw.push(referencePoints.rearfoot.maxXPoint);
      this.feetAnalysis.right.pointsToDraw.push(referencePoints.rearfoot.minXPoint);
    }

    return {
      rearfoot: referencePoints.forefoot.maxXPoint.x - referencePoints.forefoot.minXPoint.x,
      forefoot: referencePoints.rearfoot.maxXPoint.x - referencePoints.rearfoot.minXPoint.x
    }
  }

  swapFootData(leftFootData, rightFootData) {
    return {
      left: rightFootData,
      right: leftFootData
    }
  }

  mirrorPoint(point: { x: number, y: number }): { x: number, y: number } {

    return {
      x: point.x,
      y: point.y
    }
  }

  calculateWeightDistribution(leftFootData, rightFootData): { leftPercentage: number, rightPercentage: number } {
    const totalWeightLeft = leftFootData.reduce((acc, point) => acc + point.weight, 0);
    const totalWeightRight = rightFootData.reduce((acc, point) => acc + point.weight, 0);

    const totalWeight = totalWeightLeft + totalWeightRight;

    const leftPercentage = Math.round((totalWeightLeft / totalWeight) * 100);
    const rightPercentage = Math.round((totalWeightRight / totalWeight) * 100);

    return { leftPercentage, rightPercentage };
  }

  calculateFootArea(footData): number {
    let totalWeight = 0;
    const cellSizeInCentimeters = 0.02;

    footData.forEach(point => {
      totalWeight += point.weight;
    });

    if (this.analysis.type === "Static") {
      const areaInSquareCentimeters = Math.round(totalWeight * cellSizeInCentimeters);
      return areaInSquareCentimeters;
    }

    if (this.analysis.type === "Dynamic") {
      const areaInSquareCentimeters = Math.round(totalWeight * 0.0008);
      return areaInSquareCentimeters;
    }

    return 0;
  }
}
